<template>
    <div class="lms__superadmin_workspaces_edit_details">
        <template v-if="isAddMode">
            <form-select
                :name="'workspace-customer'"
                :disabled="!allowEditWorkspaceCustomer"
                :label="$t('lms.workspace.labels.customer')"
                :is-type="'select'"
                :required="true"
                :value="workspaceCustomerId"
                :v-validate="'required'"
                :data-vv-as="$t('forms.dataVvAs.customer')"
                :get-pagination-getter="'getLmsSuperadminCustomersPagination'"
                :get-items-getter="'getLmsSuperadminCustomersMappedIdNameStatus'"
                :get-items-getter-mapped="'getLmsSuperadminCustomersMappedLabelValue'"
                :get-items-dispatch="'lmsSuperadminCustomersGet'"
                :items-extra="routeQueryCustomerName && routeQueryCustomerId ? [
                    {
                        label: routeQueryCustomerName,
                        value: routeQueryCustomerId
                    }
                ] : []"
                    :item-name="['customer', 'customers']"
                    :search-queryable="true"
                    :table-labels="[
                    {
                        key: 'name',
                        title: 'Name',
                        type: 'string',
                        sortable: true,
                        queryable: true
                    }
                ]"
                @input="setWorkspaceCustomer"
                @input-table="setWorkspaceCustomer"
            />
        </template>
        <template v-else>
            <form-text
                :name="'lms-superadmin-workspace-edit-details-customer-readonly'"
                :label="'Customer'"
                :value="workspaceCustomerName"
                :read-only="true"
            ></form-text>
        </template>
        <form-text
            :class="'lms__superadmin_workspaces_edit_details_name'"
            :disabled="!user.isSuperadmin() && !user.isMasterAdministrator()"
            :name="'workspace-name'"
            :label="$t('lms.workspace.labels.name')"
            :required="true"
            :v-validate="'required|max:45'"
            :data-vv-as="$t('forms.dataVvAs.name')"
            v-model="workspaceName"
        />
        <form-checkbox
            :disabled="!user.isSuperadmin() && !user.isMasterAdministrator()"
            :name="'workspace-name-display'"
            :label="'Display workspace name in header'"
            v-model="workspaceNameDisplay"
        ></form-checkbox>
        <div class="columns is-block-mobile is-variable is-8">
            <div class="column is-6">
                <form-image
                    :disabled="!user.isSuperadmin() && !user.isMasterAdministrator() || (!user.hasSelectedProfile() && !workspaceCustomerId)"
                    :name="'workspace-logo'"
                    :label="'Workspace logo'"
                    :value="workspaceLogo"
                    :customer-id="workspaceCustomerId"
                    @emit-select="setWorkspaceLogo"
                    @emit-remove="removeWorkspaceLogo"
                >Recommendations for logo file: <br>
                    - .png extension <br>
                    - 25px of height <br>
                    - 120px of width
                </form-image>
            </div>
            <div class="column is-6">
                <div
                    class="lms__superadmin_workspaces_edit_details_preview"
                >
                    <component-wrapper>
                        <component-wrapper
                            :class="'lms__superadmin_workspaces_edit_details_preview_box'"
                            :border="true"
                        >
                            <component-title
                                :size="'tiny'"
                            >Preview
                            </component-title>
                            <div class="columns is-vcentered">
                                <div class="column is-narrow">
                                    <router-link :to="{ name: 'home' }">
                                        <component-logo
                                            :file-id="workspaceLogo"
                                            :height="workspaceLogoHeight"
                                        />
                                    </router-link>
                                </div>
                                <template v-if="workspaceNameDisplay">
                                    <div class="column is-narrow">
                                        <component-separator :vertical="true"></component-separator>
                                    </div>
                                    <div class="column">
                                        <component-text
                                            :class="'lms__superadmin_workspaces_edit_details_preview_box_workspacename'"
                                            :size="'tiny'"
                                        >{{ workspaceName }}</component-text>
                                    </div>
                                </template>
                            </div>
                        </component-wrapper>
                        <form-range
                            :label="'Logo image size'"
                            :name="'lms_superadmin-workspace-edit-details-logoheight'"
                            :min="25"
                            :max="200"
                            v-model="workspaceLogoHeight"
                        ></form-range>
                    </component-wrapper>
                </div>
            </div>
        </div>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import PopNotify from '@/components/default/shared/PopNotify'
import FormText from '@/components/default/forms/FormText'
import FormSelect from '@/components/default/forms/FormSelect'
import { performReset, performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormImage from '@/components/default/forms/FormImage'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentLogo from '@/components/default/shared/ComponentLogo'
import ComponentSeparator from '@/components/default/shared/ComponentSeparator'
import ComponentText from '@/components/default/shared/ComponentText'
import FormRange from '@/components/default/forms/FormRange'

export default {
  name: 'lms-superadmin-workspace-edit-details',
  components: {
    FormRange,
    ComponentText,
    ComponentSeparator,
    ComponentLogo,
    ComponentTitle,
    ComponentWrapper,
    FormImage,
    FormCheckbox,
    FormSelect,
    FormText,
    PopNotify
  },
  data () {
    return {
      user: new User()
    }
  },
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  computed: {
    workspaceName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceSetName', value)
      }
    },
    workspaceCustomerId: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomerId
      }
    },
    workspaceCustomerName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomerName
      }
    },
    workspaceExpirydate: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceExpirydate
      }
    },
    workspaceNameDisplay: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomisationDisplayName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceSetCustomisationDisplayName', value)
      }
    },
    workspaceLogoHeight: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomisationWorkspaceLogoHeight
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceSetCustomisationWorkspaceLogoHeight', value)
      }
    },
    workspaceLogo () {
      return this.$store.getters.getLmsSuperadminWorkspaceCustomisationWorkspaceLogo
    },
    routeQueryCustomerId () {
      return this.$route.query.customerId
    },
    routeQueryCustomerName () {
      return this.$route.query.customerName
    },
    isAddMode () {
      return this.$route.name.includes('workspace-add')
    },
    isEditMode () {
      return this.$route.name.includes('workspace-edit')
    },
    watchContent () {
      return this.workspaceName + this.workspaceExpirydate
    },
    rootItemName () {
      return 'workspace'
    },
    nameActionCreate () {
      return 'lmsSuperadminWorkspaceCreate'
    },
    nameActionUpdate () {
      return 'lmsSuperadminWorkspaceUpdate'
    },
    nameActionReset () {
      return 'lmsSuperadminWorkspaceReset'
    },
    //
    rawCustomers () {
      return this.$store.getters.getLmsSuperadminCustomers
    },
    customers () {
      let objectData = this.rawCustomers
      let objectDataArray = []
      if (objectData) {
        objectDataArray = objectData.map(function (object) {
          return {
            label: `${object.name}`,
            value: object.id
          }
        })
      }
      return objectDataArray
    },
    //
    allowEditWorkspaceCustomer () {
      const routeName = this.$route.name
      const routeQueryCustomerId = this.$route.query.customerId
      const fieldValue = this.workspaceCustomerId

      if (this.user.isSuperadmin() || (this.user.isMasterAdministrator() && this.userProfilesCustomersWithMARole.length > 1)) {
        if (routeName.includes('workspace-edit')) {
          return fieldValue === '' || fieldValue === undefined
        } else if (routeName.includes('workspace-add')) {
          return (
            routeQueryCustomerId === '' ||
          routeQueryCustomerId === undefined
          )
        } else {
          return true
        }
      } else {
        return false
      }
    },
    userProfilesCustomersWithMARole () {
      return this.$store.getters.getLmsSuperadminUserProfilesCustomersWithMARole
    }
  },
  methods: {
    setWorkspaceCustomer (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceSetCustomer', data)
      this.removeWorkspaceLogo()
    },
    setWorkspaceExpirydate (data) {
      this.$store.dispatch('lmsSuperadminWorkspaceSetExpirydate', data)
    },
    updateWorkspaceStatus (value) {
      let status = value ? 'active' : 'draft'
      this.$store.dispatch('lmsSuperadminWorkspaceSetStatus', status)
    },
    setWorkspaceLogo (value) {
      this.$store.dispatch('lmsSuperadminWorkspaceSetCustomisationWorkspaceLogo', value[0])
      this.workspaceLogoHeight = 60
    },
    removeWorkspaceLogo (value) {
      this.$store.dispatch('lmsSuperadminWorkspaceSetCustomisationWorkspaceLogo', '')
    }
  },
  watch: {
    customers () {
      if (
        this.$route.name.includes('workspace-add') &&
      this.user.isMasterAdministrator() &&
      !this.user.isSuperadmin() &&
      this.customers.length
      ) {
        this.setWorkspaceCustomer(this.customers[0])
      }
    }
  },
  mounted () {
    const routerQueryCustomerId = this.$route.query.customerId
    if (routerQueryCustomerId) {
      this.setWorkspaceCustomer({ value: routerQueryCustomerId })
    }
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/workspaces/_lmssuperadminworkspacesaddeditdetails.scss";
</style>
